/**
 * 데모 모드 전용 더미 장소 데이터.
 *
 * 실제 평택온누리교회의 내부 공간 구성과는 무관한 가상 데이터입니다.
 * 시연/스크린샷에 보기 좋도록 다양성을 위해 임의로 구성되어 있어요.
 */

export interface Place {
  plcCd: string;
  plcNm: string;
}

export interface PlacesByFloor {
  floor: string;
  places: Place[];
}

export interface SelectedPlace extends Place, Omit<PlacesByFloor, "places"> {}

// ─────────────────────────────────────────────────────────────
// 본관 (PTK_PTK)
// 5층 / 19개 장소
// ─────────────────────────────────────────────────────────────

export const ptonnuriPlaceByFloor = [
  {
    floor: "1층",
    places: [
      { plcCd: "PTK_PTK_0101", plcNm: "본당 (메인홀)" },
      { plcCd: "PTK_PTK_0102", plcNm: "카페 온누리" },
      { plcCd: "PTK_PTK_0103", plcNm: "키즈 플레이존" },
      { plcCd: "PTK_PTK_0104", plcNm: "안내 라운지" },
    ],
  },
  {
    floor: "2층",
    places: [
      { plcCd: "PTK_PTK_0201", plcNm: "새가족실" },
      { plcCd: "PTK_PTK_0202", plcNm: "운영위원회실" },
      { plcCd: "PTK_PTK_0203", plcNm: "소그룹실 A" },
      { plcCd: "PTK_PTK_0204", plcNm: "소그룹실 B" },
    ],
  },
  {
    floor: "3층",
    places: [
      { plcCd: "PTK_PTK_0301", plcNm: "미디어 스튜디오" },
      { plcCd: "PTK_PTK_0302", plcNm: "청년 라운지" },
      { plcCd: "PTK_PTK_0303", plcNm: "세미나실" },
      { plcCd: "PTK_PTK_0304", plcNm: "기도실" },
    ],
  },
  {
    floor: "4층",
    places: [
      { plcCd: "PTK_PTK_0401", plcNm: "음악 연습실" },
      { plcCd: "PTK_PTK_0402", plcNm: "키즈존 홀" },
      { plcCd: "PTK_PTK_0403", plcNm: "회의실 A" },
      { plcCd: "PTK_PTK_0404", plcNm: "회의실 B" },
    ],
  },
  {
    floor: "5층",
    places: [
      { plcCd: "PTK_PTK_0501", plcNm: "다목적홀" },
      { plcCd: "PTK_PTK_0502", plcNm: "루프탑 가든" },
      { plcCd: "PTK_PTK_0503", plcNm: "도서관" },
    ],
  },
];

// 평면 리스트 — ByFloor 와 순서/코드 일치 필수
export const ptonnuriPlace = ptonnuriPlaceByFloor.flatMap((group) =>
  group.places.map((p) => ({
    plcCd: p.plcCd,
    plcNm: p.plcNm,
    plcF: Number(group.floor.replace(/[^0-9]/g, "")),
    cpsCd: "PTK",
    bldCd: "PTK_PTK",
    crtId: "1",
    updId: "1",
  })),
);

// ─────────────────────────────────────────────────────────────
// 비전관 (PTK_PMC) — 가상 별관
// ─────────────────────────────────────────────────────────────

export const pmcPlaceByFloor = [
  {
    floor: "1층",
    places: [
      { plcCd: "PTK_PMC_0101", plcNm: "비전관 로비" },
      { plcCd: "PTK_PMC_0102", plcNm: "북카페" },
    ],
  },
  {
    floor: "2층",
    places: [
      { plcCd: "PTK_PMC_0201", plcNm: "강의실 201" },
      { plcCd: "PTK_PMC_0202", plcNm: "강의실 202" },
      { plcCd: "PTK_PMC_0203", plcNm: "스터디룸" },
    ],
  },
  {
    floor: "3층",
    places: [
      { plcCd: "PTK_PMC_0301", plcNm: "강의실 301" },
      { plcCd: "PTK_PMC_0302", plcNm: "강의실 302" },
      { plcCd: "PTK_PMC_0303", plcNm: "교사 라운지" },
    ],
  },
];

// ─────────────────────────────────────────────────────────────
// 정수 코드 기반 (구버전 호환)
// 일부 화면에서 사용 — 구조 유지 + 이름만 가상화
// ─────────────────────────────────────────────────────────────

export const placesByFloor = [
  {
    floor: "2층",
    places: [
      { plcCd: "1", plcNm: "새가족실" },
      { plcCd: "2", plcNm: "운영위원회실" },
      { plcCd: "3", plcNm: "소그룹실 A" },
      { plcCd: "4", plcNm: "소그룹실 B" },
      { plcCd: "5", plcNm: "다용도실" },
    ],
  },
  {
    floor: "3층",
    places: [
      { plcCd: "6", plcNm: "미디어 스튜디오" },
      { plcCd: "7", plcNm: "청년 라운지" },
      { plcCd: "8", plcNm: "세미나실" },
    ],
  },
  {
    floor: "4층",
    places: [
      { plcCd: "9", plcNm: "음악 연습실" },
      { plcCd: "10", plcNm: "키즈존 홀" },
      { plcCd: "11", plcNm: "회의실 A" },
      { plcCd: "12", plcNm: "회의실 B" },
    ],
  },
  {
    floor: "5층",
    places: [
      { plcCd: "13", plcNm: "다목적홀" },
      { plcCd: "14", plcNm: "루프탑 가든" },
    ],
  },
];

// 데모상 "이미 예약된" 표시용 장소
export const reservedPlaces = [
  {
    plcCd: "PTK_PTK_0201",
    plcNm: "새가족실",
    plcF: 2,
    cpsCd: "PTK",
    bldCd: "PTK_PTK",
  },
  {
    plcCd: "PTK_PTK_0501",
    plcNm: "다목적홀",
    plcF: 5,
    cpsCd: "PTK",
    bldCd: "PTK_PTK",
  },
];
